// src/CleaningEvents.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { signInWithRedirect } from 'aws-amplify/auth';
import { useSession } from './auth/SessionProvider.jsx';
import { api } from './lib/apiFetch';
import DetailsView from './DetailsView.jsx';

// date-fns helpers
import { parse as parseDate, differenceInWeeks, differenceInMonths, isValid } from 'date-fns';

// API paths (relative; api helper adds base URL + JWT)
const LIST_PATH   = '/alltrucks?type=1000';
const UPDATE_PATH = '/updatecleaningreport';

// Parse 'MM/DD/YYYY' safely
const parseUS = (s) => {
  if (!s || typeof s !== 'string') return null;
  const d = parseDate(s, 'MM/dd/yyyy', new Date());
  return isValid(d) ? d : null;
};

export default function CleaningEvents() {
  const { signedIn } = useSession();

  // State
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedCleaningItem, setSelectedCleaningItem] = useState(null);
  const [checkboxStates, setCheckboxStates] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'unitNumber', direction: 'ascending' });
  const [searchTerm, setSearchTerm] = useState('');
  const [timeframe, setTimeframe] = useState('2 weeks');
  const [isDetailsVisible, setIsDetailsVisible] = useState(false);
  const [selectedAction, setSelectedAction] = useState('');
  const [error, setError] = useState('');

  // Fetch once on mount (only if signed in)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!signedIn) return; // show sign-in prompt in render
      setError('');
      try {
        const data = await api.get(LIST_PATH);
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setItems(list);
        // init all checkboxes to false
        const initial = {};
        for (const it of list) initial[it.id] = false;
        setCheckboxStates(initial);
      } catch (e) {
        if (!cancelled) setError(String(e?.message || e));
      }
    };
    run();
    return () => { cancelled = true; };
  }, [signedIn]);

  // Filter when items/timeframe change
  useEffect(() => {
    const now = new Date();
    const filtered = items.filter((item) => {
      const d = parseUS(item?.dateCleaned);
      if (!d) return timeframe === 'all'; // keep only in "all" if no date
      switch (timeframe) {
        case '2 weeks':  return differenceInWeeks(now, d) <= 2;
        case '4 weeks':  return differenceInWeeks(now, d) <= 4;
        case '3 months': return differenceInMonths(now, d) <= 3;
        case '6 months': return differenceInMonths(now, d) <= 6;
        case 'all':      return true;
        default:         return true;
      }
    });
    setFilteredItems(filtered);
  }, [timeframe, items]);

  // Sorting + search
  const sortedItems = useMemo(() => {
    const byTerm = (v) => (v || '').toString().toLowerCase();
    const term = searchTerm.toLowerCase();

    const visible = filteredItems.filter((item) =>
      byTerm(item.unitNumber).includes(term) ||
      byTerm(item.truckCond).includes(term)  ||
      byTerm(item.dateCleaned).includes(term)||
      byTerm(item.cleanedBy).includes(term)  ||
      byTerm(item.cleaningType).includes(term) ||
      byTerm(item.paid).includes(term)
    );

    if (!sortConfig?.key) return visible;

    const arr = [...visible];
    arr.sort((a, b) => {
      const A = byTerm(a[sortConfig.key]);
      const B = byTerm(b[sortConfig.key]);
      if (A < B) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (A > B) return sortConfig.direction === 'ascending' ?  1 : -1;
      return 0;
    });
    return arr;
  }, [filteredItems, sortConfig, searchTerm]);

  // Handlers
  const handleCheckboxChange = (item) => {
    setCheckboxStates((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
  };

  const handleTimeframeChange = (e) => setTimeframe(e.target.value);

  const requestSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key && prev.direction === 'ascending') {
        return { key, direction: 'descending' };
      }
      return { key, direction: 'ascending' };
    });
  };

  const handleMarkAsPaid = async () => {
    if (!signedIn) {
      alert('Please sign in to mark items as paid.');
      return;
    }

    // Only checked + currently "Invoiced"
    const toMark = items.filter((it) => checkboxStates[it.id] && it.paid === 'Invoiced');
    if (toMark.length === 0) return;

    // Back-end expects full items with updated paid date
    const currentDate = new Date();
    const mm = String(currentDate.getMonth() + 1).padStart(2, '0');
    const dd = String(currentDate.getDate()).padStart(2, '0');
    const yyyy = currentDate.getFullYear();
    const paidStr = `${mm}/${dd}/${yyyy}`;

    const payload = toMark.map((it) => ({ ...it, paid: paidStr }));

    try {
      await api.post(UPDATE_PATH, payload);

      // Optimistically update UI and uncheck
      setItems((curr) =>
        curr.map((it) => (payload.find((p) => p.id === it.id) ? { ...it, paid: paidStr } : it))
      );
      setCheckboxStates((prev) => {
        const next = { ...prev };
        for (const it of toMark) next[it.id] = false;
        return next;
      });
    } catch (e) {
      console.error('Mark paid failed:', e);
      alert(`Error: ${e?.message || e}`);
    }
  };

  const handleViewDetails = (item) => {
    setSelectedCleaningItem(item);
    setIsDetailsVisible(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsVisible(false);
    setSelectedCleaningItem(null);
    setSelectedAction('');
    // reset all checkboxes
    setCheckboxStates((prev) =>
      Object.fromEntries(Object.keys(prev).map((k) => [k, false]))
    );
  };

  const handleActionChange = (event) => {
    const action = event.target.value;
    setSelectedAction(action);
    switch (action) {
      case 'markPaid': {
        if (!isAllSelectedPending) {
          alert('Only items with pending payments can be marked as paid.');
          return;
        }
        void handleMarkAsPaid();
        break;
      }
      case 'viewDetails': {
        const selectedItem = items.find((it) => checkboxStates[it.id]);
        if (selectedItem) handleViewDetails(selectedItem);
        break;
      }
      default:
        // no-op
        break;
    }
  };

  // Derived UI state
  const isAnySelected = Object.values(checkboxStates).some(Boolean);
  const isAllSelectedPending = items.every((it) =>
    checkboxStates[it.id] ? it.paid === 'Invoiced' : true
  );
  const numberOfSelectedItems = Object.values(checkboxStates).filter(Boolean).length;

  return (
    <div className="container listDiv">
      {!signedIn && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ color: 'crimson', marginBottom: 8 }}>
            You must sign in to view and manage cleaning events.
          </div>
          <button className="link-btn" onClick={() => signInWithRedirect()}>
            Sign in
          </button>
        </div>
      )}

      {error && (
        <div style={{ color: 'crimson', marginBottom: 8 }}>
          {error}
        </div>
      )}

      <div className="search-and-add-bar">
        <div>
          <label className="input-label">Timeframe</label>
          <select
            value={timeframe}
            onChange={handleTimeframeChange}
            className="timeframe-dropdown"
            disabled={!signedIn}
          >
            <option value="2 weeks">2 Weeks</option>
            <option value="4 weeks">4 Weeks</option>
            <option value="3 months">3 Months</option>
            <option value="6 months">6 Months</option>
            <option value="all">All</option>
          </select>
        </div>

        <div>
          <label className="input-label">Search</label>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={!signedIn}
          />
        </div>

        <div>
          <label className="input-label">Actions</label>
          <select
            disabled={!signedIn || !isAnySelected}
            className="action-dropdown"
            value={selectedAction}
            onChange={handleActionChange}
          >
            <option value="">Select Action</option>
            <option value="markPaid" disabled={!isAllSelectedPending}>Mark Paid</option>
            <option value="viewDetails" disabled={numberOfSelectedItems !== 1}>View Details</option>
          </select>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th className="checkbox-header"></th>
              <th className="styleCol1" onClick={() => requestSort('unitNumber')}>Unit Number</th>
              <th className="styleCol1" onClick={() => requestSort('truckCond')}>Truck Condition</th>
              <th className="styleCol1" onClick={() => requestSort('dateCleaned')}>Last Cleaned</th>
              <th className="styleCol1" onClick={() => requestSort('cleaningType')}>Type</th>
              <th className="styleCol1" onClick={() => requestSort('cleanedBy')}>Detailer</th>
              <th className="styleCol1" onClick={() => requestSort('paid')}>Paid</th>
            </tr>
          </thead>
          <tbody className="scrollable-tbody">
            {sortedItems.map((item) => (
              <tr key={item.id}>
                <td className="checkbox-cell">
                  <input
                    type="checkbox"
                    checked={checkboxStates[item.id] || false}
                    onChange={() => handleCheckboxChange(item)}
                    disabled={!signedIn}
                  />
                </td>
                <td className="styleCol1">{item.unitNumber}</td>
                <td className="styleCol1">{item.truckCond}</td>
                <td className="styleCol1">{item.dateCleaned}</td>
                <td className="styleCol1">{item.cleaningType}</td>
                <td className="styleCol1">{item.cleanedBy}</td>
                <td className="styleCol1">{item.paid}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isDetailsVisible && selectedCleaningItem && (
        <DetailsView item={selectedCleaningItem} onClose={handleCloseDetails} />
      )}
    </div>
  );
}
